import {
    Check,
    Circle,
    Clear,
    ContentCopyOutlined,
    DeleteForeverOutlined,
    Edit,
    KeyboardArrowDown,
    KeyboardArrowUp,
    Lock,
    PauseOutlined,
    PlayArrowOutlined,
    PriorityHigh,
    PublicOutlined,
    SwapCallsOutlined,
    TimerOutlined,
    VpnLockOutlined,
} from "@mui/icons-material";
import {
    GetV2Channel_getV2Channel,
    GetV2Channel_getV2Channel_distributions,
    GetV2Channel_getV2Channel_engines,
} from "./__generated__/GetV2Channel";
import { useEffect, useState } from "react";
import { AllowDenyMode, KeyValuePairInput } from "../../../../__generated__/globalTypes";
import AuthService from "../../../../auth/AuthService";
import DiscoveryExplorerDialog from "./DiscoveryExplorerDialog";
import { Tooltip } from "@mui/material";
import { useMutation } from "react-apollo";
import {
    DeleteV2DistributionMutation,
    DeleteV2DistributionMutationVariables,
} from "./__generated__/DeleteV2DistributionMutation";
import { DELETE_V2_DISTRIBUTION, GET_V2_CHANNEL } from "./V2ChannelDetails";
import { toast } from "react-toastify";
import { GraphQLError } from "graphql";
import ConfirmDeleteDialog from "./ConfirmDeleteDialog";
import UpdateDistributionPlayerSettingDialog from "./Distributions/UpdateDistributionPlayerSettingsDialog";
import gql from "graphql-tag";
import UpdateDistributionEndpointSettingsDialog from "./Distributions/UpdateDistributionEndpointSettingsDialog";
import UpdateDistributionNameDialog from "./Distributions/UpdateDistributionNameDialog";
import DistributionGeoBlockingDialog from "./Distributions/DistributionGeoBlockingDialog";
import DistributionIpBlockingDialog from "./Distributions/DistributionIpBlockingDialog";
import DistributionJwtDialog from "./Distributions/DistributionJwtDialog";
import UpdateDistributionParametersDialog from "./Distributions/DistributionParamsDialog";
import UpdateDistributionOutputsDialog from "./Engine/UpdateEngineOutputsDialog";
import CloneDistributionDialog from "./Distributions/CloneDistributionDialog";
import DistributionRefererBlockingDialog from "./Distributions/DistributionRefererBlockingDialog";
import { UpdateV2Distribution, UpdateV2DistributionVariables } from "./__generated__/UpdateV2Distribution";
import DistributionWebRtcDialog from "./Distributions/DistributionWebRtcDialog";
import DistributionDvrDialog from "./Distributions/DistributionDvrDialog";

interface Props {
    distribution: Omit<GetV2Channel_getV2Channel_distributions, "__typename">;
    engines: GetV2Channel_getV2Channel_engines[];
    channel: GetV2Channel_getV2Channel;
    channelId: string;
    organizationId: string;
    onHoverEngine: (engineId: string) => void;
    onStopHoverEngine: () => void;
}

export default function V2ChannelDistributionDetails({
    distribution: inputDist,
    engines,
    onHoverEngine,
    channel,
    onStopHoverEngine,
    channelId,
    organizationId,
}: Props) {
    const [showMore, setShowMore] = useState<boolean>(false);

    const isAdmin = AuthService.isTHEOAdminSales();
    const [showExplorer, setShowExplorer] = useState<boolean>(false);
    const [openOutputs, setOpenOutputs] = useState<boolean>(false);

    const [distribution, setDistribution] =
        useState<Omit<GetV2Channel_getV2Channel_distributions, "__typename">>(inputDist);

    useEffect(() => {
        setDistribution(inputDist);
    }, [inputDist]);

    const [deleteDistribution] = useMutation<DeleteV2DistributionMutation, DeleteV2DistributionMutationVariables>(
        DELETE_V2_DISTRIBUTION,
    );

    const [updateDistribution] = useMutation<UpdateV2Distribution, UpdateV2DistributionVariables>(UPDATE_DISTRIBUTION);

    const [openConfirmDelete, setOpenConfirmDelete] = useState<boolean>(false);
    const [openConfirmPauseJwt, setOpenConfirmPauseJwt] = useState<boolean>(false);
    const [openEditPlayer, setOpenEditPlayer] = useState<boolean>(false);
    const [openEditEndpoints, setOpenEditEndpoints] = useState<boolean>(false);
    const [openEditName, setOpenEditName] = useState<boolean>(false);
    const [openCloneDistribution, setOpenCloneDistribution] = useState<boolean>(false);

    const [openGeoBlocking, setOpenGeoBlocking] = useState<boolean>(false);
    const [openIpBlocking, setOpenIpBlocking] = useState<boolean>(false);
    const [openRefererBlocking, setOpenRefererBlocking] = useState<boolean>(false);

    const [openKeys, setOpenKeys] = useState<boolean>(false);
    const [openParams, setOpenParams] = useState<boolean>(false);

    const [openWebRtc, setOpenWebRtc] = useState<boolean>(false);
    const [openDvr, setOpenDvr] = useState<boolean>(false);

    async function onConfirmEngineDelete() {
        try {
            await deleteDistribution({
                variables: { input: { id: distribution.id, organizationId } },
                refetchQueries: [{ query: GET_V2_CHANNEL, variables: { organizationId, channelId } }],
            });
            toast.success("Distribution deleted");
        } catch (e) {
            if (e instanceof GraphQLError) {
                toast.error(e.message);
            } else {
                toast.error("Unknown error");
            }
        }
        setOpenConfirmDelete(false);
    }

    async function onConfirmPauseJwtSecurity(): Promise<void> {
        try {
            await updateDistribution({
                variables: {
                    input: {
                        id: distribution.id,
                        organizationId,
                        updates: {
                            security: {
                                skipJwtChecks: !distribution.security.skipJwtChecks,
                            },
                        },
                    },
                },
                refetchQueries: [
                    {
                        query: GET_V2_CHANNEL,
                        variables: {
                            organizationId,
                            channelId,
                        },
                    },
                ],
            });
            setDistribution({
                ...distribution,
                security: {
                    ...distribution.security,
                    skipJwtChecks: !distribution.security.skipJwtChecks,
                },
            });
        } catch (e) {
            if (e instanceof GraphQLError) {
                toast.error(e.message);
            } else {
                toast.error("Unknown error");
            }
        }
        setOpenConfirmPauseJwt(false);
    }

    return (
        <div className="flex flex-row justify-between w-full rounded-md border-[1px] border-slate-300 px-2 py-2">
            <div className="flex flex-col w-full">
                <div className="flex flex-row gap-1 justify-between">
                    <div className="flex flex-row gap-1 items-center">
                        <div className={`${distribution.enabled ? "text-green-500" : "text-red-500"} mt-[-4px]`}>
                            <Circle style={{ fontSize: "8px" }} />
                        </div>
                        <div className="font-semibold mb-1">{distribution.name}</div>
                        <div>
                            <div className="bg-fuchsia-50 text-fuchsia-700 p-[1px] px-1 text-[11px] rounded-md ml-1">
                                {distribution.endpoints.engineIds.length} engine(s)
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-row gap-1">
                        <Tooltip title={"Edit"}>
                            <button
                                className="text-blue-900 hover:text-blue-800  rounded-md px-1 py-[2px] text-xs disabled:text-slate-300"
                                onClick={() => setOpenEditName(true)}
                            >
                                <Edit style={{ fontSize: "14px" }} />
                            </button>
                        </Tooltip>
                        {isAdmin && (
                            <Tooltip title={"Clone"}>
                                <button
                                    className="text-blue-900 hover:text-blue-800  rounded-md px-1 py-[2px] text-xs disabled:text-slate-300"
                                    onClick={() => setOpenCloneDistribution(true)}
                                >
                                    <ContentCopyOutlined style={{ fontSize: "14px" }} />
                                </button>
                            </Tooltip>
                        )}
                        <Tooltip title={"Delete"}>
                            <button
                                onClick={() => setOpenConfirmDelete(true)}
                                className="text-red-500 hover:text-red-600 rounded-md px-1 py-[2px] text-xs  disabled:text-slate-300"
                            >
                                <DeleteForeverOutlined style={{ fontSize: "14px" }} />
                            </button>
                        </Tooltip>
                    </div>
                </div>
                <div className="flex flex-row gap-2 text-xs items-center text-[11px] text-gray-500 ">
                    <div className="text-gray-500">{distribution.id}</div>
                    {distribution.security.geoBlocking.enabled && (
                        <div className="flex flex-row items-center gap-1 ">
                            {distribution.security.geoBlocking.mode === AllowDenyMode.allow ? (
                                <PublicOutlined color="inherit" style={{ fontSize: "13px" }} />
                            ) : (
                                <VpnLockOutlined color="inherit" style={{ fontSize: "13px" }} />
                            )}{" "}
                            Geo blocking: {distribution.security.geoBlocking.mode}
                        </div>
                    )}
                    {distribution.security.ipBlocking.enabled && (
                        <div className="flex flex-row items-center gap-1 ">
                            {distribution.security.ipBlocking.mode === AllowDenyMode.allow ? (
                                <PublicOutlined color="inherit" style={{ fontSize: "13px" }} />
                            ) : (
                                <VpnLockOutlined color="inherit" style={{ fontSize: "13px" }} />
                            )}{" "}
                            IP blocking: {distribution.security.ipBlocking.mode}
                        </div>
                    )}
                    {distribution.security.refererBlocking.enabled && (
                        <div className="flex flex-row items-center gap-1 ">
                            <SwapCallsOutlined color="inherit" style={{ fontSize: "13px" }} /> Referer blocking enabled
                        </div>
                    )}
                    <div className="flex flex-row items-center gap-1 ">
                        {distribution.security.keys.length > 0 && (
                            <>
                                <Lock color="inherit" style={{ fontSize: "13px" }} />
                                JWT secured
                            </>
                        )}
                        {distribution.security.skipJwtChecks && isAdmin && (
                            <div className="text-orange-500">
                                <PriorityHigh color="inherit" style={{ fontSize: "13px" }} />
                                Skipping JWT checks
                            </div>
                        )}
                    </div>
                    <div className="flex flex-row items-center gap-1 ml-[-6px] ">
                        <TimerOutlined color="inherit" style={{ fontSize: "13px" }} />
                        {distribution.targetLatency ? <>{distribution.targetLatency} second(s)</> : <>sub-second</>}
                    </div>
                </div>

                {showMore && (
                    <div className="rounded-md border-[1px] bg-gray-50 flex flex-col md:flex-row gap-6 mt-2 px-2 py-1 text-[13px] w-full">
                        <div className="w-full flex-col flex gap-1">
                            <div className="flex flex-row justify-between items-center">
                                <div className="font-semibold mt-2">Endpoints</div>
                                <Tooltip title={"Edit"}>
                                    <button
                                        onClick={() => setOpenEditEndpoints(true)}
                                        className="text-blue-900 hover:text-blue-800  rounded-md px-1 py-[2px] text-xs disabled:text-slate-300"
                                    >
                                        <Edit style={{ fontSize: "14px" }} />
                                    </button>
                                </Tooltip>
                            </div>
                            <div className="bg-white rounded-md p-2 border-[1px] mt-2 flex flex-col">
                                <div className="font-semibold mb-1">CDN distribution</div>
                                <div className="flex flex-row gap-2 flex-wrap mb-3">
                                    {distribution.endpoints.cdns.fastly && (
                                        <div className="rounded bg-slate-100 text-black p-1">
                                            Fastly: {distribution.endpoints.cdns.fastly.weight}
                                        </div>
                                    )}
                                    {distribution.endpoints.cdns.akamai && (
                                        <div className="rounded bg-slate-100 text-black p-1">
                                            Akamai: {distribution.endpoints.cdns.akamai.weight}
                                        </div>
                                    )}
                                </div>

                                <div className="font-semibold mb-1">Engines</div>
                                <div className="flex flex-row gap-2 flex-wrap">
                                    {distribution.endpoints.engineIds.map((engineId) => (
                                        <div
                                            className="rounded bg-fuchsia-50 text-fuchsia-700 p-1"
                                            onMouseEnter={() => onHoverEngine(engineId)}
                                            onMouseLeave={() => onStopHoverEngine()}
                                            key={`engine-${engineId}`}
                                        >
                                            {engines.find(({ id }) => id === engineId)?.name}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="flex flex-row justify-between">
                                <div className="font-semibold mt-2">Player</div>
                                <Tooltip title={"Edit"}>
                                    <button
                                        className="text-blue-900 hover:text-blue-800  rounded-md px-1 py-[2px] text-xs disabled:text-slate-300"
                                        onClick={() => setOpenEditPlayer(true)}
                                    >
                                        <Edit style={{ fontSize: "14px" }} />
                                    </button>
                                </Tooltip>
                            </div>
                            <div className="bg-white rounded-md p-2 border-[1px] flex flex-col">
                                <div className="mb-1">
                                    <strong>Target latency:</strong>{" "}
                                    {distribution.targetLatency
                                        ? `${distribution.targetLatency} second(s)`
                                        : "sub-second"}
                                </div>
                            </div>
                            <div className="flex flex-row justify-between">
                                <div className="font-semibold mt-2">Parameters</div>
                                <Tooltip title={"Edit"}>
                                    <button
                                        className="text-blue-900 hover:text-blue-800  rounded-md px-1 py-[2px] text-xs disabled:text-slate-300"
                                        onClick={() => setOpenParams(true)}
                                    >
                                        <Edit style={{ fontSize: "14px" }} />
                                    </button>
                                </Tooltip>
                            </div>

                            {isAdmin && (
                                <>
                                    <div className="bg-white rounded-md p-2 border-[1px] flex flex-col">
                                        <div className="mb-1">
                                            {distribution.parameters.length === 0 ? (
                                                <>No parameters set</>
                                            ) : (
                                                <div className="flex flex-row gap-2 flex-wrap">
                                                    {distribution.parameters.map((param, i) => (
                                                        <div className="bg-slate-100 rounded-xl px-2 py-[4px] font-mono">
                                                            {param.key}:{param.value}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <div className="font-semibold mt-2 mb-1">Discovery</div>
                                        <div className="flex flex-row gap-3">
                                            <button
                                                className="rounded-md px-3 py-1 bg-yellow-300 hover:bg-yellow-200 font-semibold"
                                                onClick={() => setShowExplorer(true)}
                                            >
                                                Open discovery explorer
                                            </button>
                                            <button
                                                className="rounded-md px-3 py-1 bg-yellow-300 hover:bg-yellow-200 font-semibold"
                                                onClick={() =>
                                                    window.open(
                                                        `https://demo.theo.live/?distribution=${distribution.id}`,
                                                    )
                                                }
                                            >
                                                Open demo player
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="w-full flex-col flex gap-1">
                            <div className="font-semibold py-1">Security</div>
                            <div className="bg-white rounded-md p-2 border-[1px] flex flex-col gap-4">
                                <div className="flex flex-col bg-slate-100 py-2  gap-2 px-2 rounded-md">
                                    <div className="flex  flex-row gap-1 items-center justify-between">
                                        <div className="flex  flex-row gap-2 items-center">
                                            <div
                                                className={`${
                                                    distribution.security.geoBlocking.enabled
                                                        ? "bg-green-50 text-green-500"
                                                        : "bg-red-50 text-red-500"
                                                }  font-semibold text-xs p-1 w-16 flex items-center justify-center`}
                                            >
                                                {distribution.security.geoBlocking.enabled ? "Enabled" : "Disabled"}
                                            </div>
                                            <div className="mb font-semibold">Geo blocking</div>
                                        </div>
                                        <Tooltip title={"Edit"}>
                                            <button
                                                className="text-blue-900 hover:text-blue-800  rounded-md px-1 py-[2px] text-xs disabled:text-slate-300"
                                                onClick={() => setOpenGeoBlocking(true)}
                                            >
                                                <Edit style={{ fontSize: "14px" }} />
                                            </button>
                                        </Tooltip>
                                    </div>
                                    {distribution.security.geoBlocking.enabled && (
                                        <div className="bg-white p-2 rounded-md flex flex-col gap-1">
                                            <div>
                                                <strong>Mode: </strong>
                                                {distribution.security.geoBlocking.mode}
                                            </div>
                                            <div>
                                                <strong>Countries: </strong>
                                                {distribution.security.geoBlocking.countries.join(", ")}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-col bg-slate-100 py-2  gap-2 px-2 rounded-md">
                                    <div className="flex  flex-row gap-1 items-center justify-between">
                                        <div className="flex  flex-row gap-2 items-center">
                                            <div
                                                className={`${
                                                    distribution.security.ipBlocking.enabled
                                                        ? "bg-green-50 text-green-500"
                                                        : "bg-red-50 text-red-500"
                                                }  font-semibold text-xs p-1 w-16 flex items-center justify-center`}
                                            >
                                                {distribution.security.ipBlocking.enabled ? "Enabled" : "Disabled"}
                                            </div>
                                            <div className="mb font-semibold">IP blocking</div>
                                        </div>
                                        <Tooltip title={"Edit"}>
                                            <button
                                                className="text-blue-900 hover:text-blue-800  rounded-md px-1 py-[2px] text-xs disabled:text-slate-300"
                                                onClick={() => setOpenIpBlocking(true)}
                                            >
                                                <Edit style={{ fontSize: "14px" }} />
                                            </button>
                                        </Tooltip>
                                    </div>
                                    {distribution.security.ipBlocking.enabled && (
                                        <div className="bg-white p-2 rounded-md flex flex-col gap-1">
                                            <div>
                                                <strong>Mode: </strong>
                                                {distribution.security.ipBlocking.mode}
                                            </div>
                                            <div>
                                                <strong>CIDRS: </strong>
                                                {distribution.security.ipBlocking.cidrs.join(", ")}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-col bg-slate-100 py-2  gap-2 px-2 rounded-md">
                                    <div className="flex  flex-row gap-1 items-center justify-between">
                                        <div className="flex  flex-row gap-2 items-center">
                                            <div
                                                className={`${
                                                    distribution.security.refererBlocking.enabled
                                                        ? "bg-green-50 text-green-500"
                                                        : "bg-red-50 text-red-500"
                                                }  font-semibold text-xs p-1 w-16 flex items-center justify-center`}
                                            >
                                                {distribution.security.refererBlocking.enabled ? "Enabled" : "Disabled"}
                                            </div>
                                            <div className="mb font-semibold">Referer blocking</div>
                                        </div>
                                        <Tooltip title={"Edit"}>
                                            <button
                                                className="text-blue-900 hover:text-blue-800  rounded-md px-1 py-[2px] text-xs disabled:text-slate-300"
                                                onClick={() => setOpenRefererBlocking(true)}
                                            >
                                                <Edit style={{ fontSize: "14px" }} />
                                            </button>
                                        </Tooltip>
                                    </div>
                                    {distribution.security.refererBlocking.enabled && (
                                        <div className="bg-white p-2 rounded-md flex flex-col gap-1">
                                            <div>
                                                <strong>Allowed domains: </strong>
                                                {distribution.security.refererBlocking.allowedDomains.join(", ")}
                                            </div>
                                            <div>
                                                <strong>Allow native apps: </strong>
                                                {distribution.security.refererBlocking.allowNativeApps ? "Yes" : "No"}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-col bg-slate-100 py-2  gap-2 px-2 rounded-md">
                                    <div className="flex  flex-row gap-1 items-center justify-between">
                                        <div className="flex  flex-row gap-2 items-center">
                                            <div
                                                className={`${
                                                    distribution.security.keys.length > 0
                                                        ? "bg-green-50 text-green-500"
                                                        : "bg-red-50 text-red-500"
                                                }  font-semibold text-xs p-1 w-16 flex items-center justify-center`}
                                            >
                                                {distribution.security.keys.length > 0 ? "Enabled" : "Disabled"}
                                            </div>
                                            <div className="mb font-semibold">JWT security</div>
                                        </div>
                                        <Tooltip title={"Edit"}>
                                            <button
                                                onClick={() => setOpenKeys(true)}
                                                className="text-blue-900 hover:text-blue-800  rounded-md px-1 py-[2px] text-xs disabled:text-slate-300"
                                            >
                                                <Edit style={{ fontSize: "14px" }} />
                                            </button>
                                        </Tooltip>
                                    </div>
                                    {distribution.security.keys.length > 0 && (
                                        <div className="bg-white p-2 rounded-md flex flex-col gap-1">
                                            <div className="flex flex-row gap-2 justify-between w-full">
                                                <div>
                                                    {" "}
                                                    <strong>Keys: </strong>{" "}
                                                </div>
                                                {isAdmin && (
                                                    <div>
                                                        <button
                                                            className="px-2 py-1 rounded-md bg-blue-500 hover:bg-blue-400 text-white flex flex-row items-center gap-1"
                                                            onClick={() => setOpenConfirmPauseJwt(true)}
                                                        >
                                                            {distribution.security.skipJwtChecks ? (
                                                                <PlayArrowOutlined
                                                                    color="inherit"
                                                                    style={{ fontSize: "14px" }}
                                                                />
                                                            ) : (
                                                                <PauseOutlined
                                                                    color="inherit"
                                                                    style={{ fontSize: "14px" }}
                                                                />
                                                            )}
                                                            {distribution.security.skipJwtChecks
                                                                ? "Resume JWT security"
                                                                : "Pause JWT security"}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex flex-row gap-2 flex-wrap">
                                                {distribution.security.keys.map((key, i) => (
                                                    <div className="rounded-md border-[1px] w-56 px-2 py-1 flex flex-col">
                                                        <div>{key.name}</div>
                                                        <div className="text-gray-500 text-[10px]">{key.id}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div>
                                <div className="flex flex-row gap-4 mt-2 justify-between">
                                    <div className="font-semibold">Outputs</div>
                                    <button
                                        className="text-blue-900 hover:text-blue-800  rounded-md px-1 py-[2px] text-xs disabled:text-slate-300"
                                        onClick={() => setOpenOutputs(true)}
                                    >
                                        <Edit style={{ fontSize: "14px" }} />
                                    </button>
                                </div>
                                <div className="bg-white flex flex-row p-1 flex-wrap gap-2">
                                    <div
                                        className={`text-xs flex flex-row items-center gap-1 ${
                                            distribution.outputs.hesp
                                                ? "bg-green-50 text-green-600 "
                                                : "bg-red-50 text-red-500"
                                        }  px-2 py-[4px] rounded-md`}
                                    >
                                        {distribution.outputs.hesp ? (
                                            <Check color="success" style={{ fontSize: "15px" }} />
                                        ) : (
                                            <Clear color="error" style={{ fontSize: "15px" }} />
                                        )}
                                        HESP
                                    </div>

                                    <div
                                        className={`text-xs flex flex-row items-center gap-1 ${
                                            distribution.outputs.hls
                                                ? "bg-green-50 text-green-600 "
                                                : "bg-red-50 text-red-500"
                                        }  px-2 py-[4px] rounded-md`}
                                    >
                                        {distribution.outputs.hls ? (
                                            <Check color="success" style={{ fontSize: "15px" }} />
                                        ) : (
                                            <Clear color="error" style={{ fontSize: "15px" }} />
                                        )}
                                        HLS
                                    </div>

                                    <div
                                        className={`text-xs flex flex-row items-center gap-1 ${
                                            distribution.outputs.hlsMpegTs
                                                ? "bg-green-50 text-green-600 "
                                                : "bg-red-50 text-red-500"
                                        }  px-2 py-[4px] rounded-md`}
                                    >
                                        {distribution.outputs.hlsMpegTs ? (
                                            <Check color="success" style={{ fontSize: "15px" }} />
                                        ) : (
                                            <Clear color="error" style={{ fontSize: "15px" }} />
                                        )}
                                        HLS MPEG-TS
                                    </div>
                                </div>
                                <div className="flex flex-row justify-between">
                                    <div className="font-semibold mt-2">WebRTC endpoint</div>
                                    <Tooltip title={"Edit"}>
                                        <button
                                            className="text-blue-900 hover:text-blue-800  rounded-md px-1 py-[2px] text-xs disabled:text-slate-300"
                                            onClick={() => setOpenWebRtc(true)}
                                        >
                                            <Edit style={{ fontSize: "14px" }} />
                                        </button>
                                    </Tooltip>
                                </div>
                                <div className="bg-white flex flex-row p-1 flex-wrap gap-2">
                                    {distribution.webRtc ? (
                                        <div className="flex flex-col">
                                            <div>
                                                <strong>Priority:</strong> {distribution.webRtc.priority}
                                            </div>
                                            <div>
                                                <strong>Stream name:</strong> {distribution.webRtc.src.name}
                                            </div>
                                            <div>
                                                <strong>Account ID:</strong> {distribution.webRtc.src.accountId}
                                            </div>
                                            <div>
                                                <strong>API URL:</strong> {distribution.webRtc.src.apiUrl}
                                            </div>
                                        </div>
                                    ) : (
                                        <div>Disabled</div>
                                    )}
                                </div>
                                {channel.dvr?.enabled === true && (
                                    <>
                                        <div className="flex flex-row justify-between">
                                            <div className="font-semibold mt-2">DVR</div>
                                            <Tooltip title={"Edit"}>
                                                <button
                                                    className="text-blue-900 hover:text-blue-800  rounded-md px-1 py-[2px] text-xs disabled:text-slate-300"
                                                    onClick={() => setOpenDvr(true)}
                                                >
                                                    <Edit style={{ fontSize: "14px" }} />
                                                </button>
                                            </Tooltip>
                                        </div>
                                        <div className="bg-white flex flex-row p-1 flex-wrap gap-2">
                                            {distribution.dvr?.enabled === true ? (
                                                <div>Enabled</div>
                                            ) : (
                                                <div>Disabled</div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <DiscoveryExplorerDialog
                open={showExplorer}
                onClose={() => setShowExplorer(false)}
                channelId={channelId}
                organizationId={organizationId}
                distributionId={distribution.id}
            />
            <ConfirmDeleteDialog
                title="Delete distribution"
                content={
                    <div>
                        Are you sure you want to delete the distribution <strong>{distribution.name}</strong> (
                        <span className="font-mono">{distribution.id}</span>)?
                    </div>
                }
                open={openConfirmDelete}
                onClose={() => setOpenConfirmDelete(false)}
                onConfirm={() => onConfirmEngineDelete()}
            />
            <UpdateDistributionPlayerSettingDialog
                distribution={distribution}
                channelId={channelId}
                open={openEditPlayer}
                onClose={() => setOpenEditPlayer(false)}
                onConfirm={(targetLatency) => {
                    setOpenEditPlayer(false);
                    setDistribution({
                        ...distribution,
                        targetLatency,
                    });
                }}
                organizationId={organizationId}
            />
            <UpdateDistributionEndpointSettingsDialog
                distribution={distribution}
                engines={engines}
                channelId={channelId}
                onClose={() => setOpenEditEndpoints(false)}
                onConfirm={(cdns, engineIds) => {
                    setOpenEditEndpoints(false);
                    setDistribution({
                        ...distribution,
                        endpoints: {
                            ...distribution.endpoints,
                            cdns: {
                                __typename: "DistributionEndpointsCdns",
                                akamai: cdns.akamai
                                    ? { weight: cdns.akamai!.weight, __typename: "DistributionEndpointsCdn" }
                                    : null,
                                fastly: cdns.fastly
                                    ? { weight: cdns.fastly!.weight, __typename: "DistributionEndpointsCdn" }
                                    : null,
                            },
                            engineIds,
                        },
                    });
                }}
                open={openEditEndpoints}
                organizationId={organizationId}
            />
            <UpdateDistributionNameDialog
                distribution={distribution}
                channelId={channelId}
                onClose={() => setOpenEditName(false)}
                onConfirm={(name: string) => {
                    setOpenEditName(false);
                    setDistribution({
                        ...distribution,
                        name,
                    });
                }}
                open={openEditName}
                organizationId={organizationId}
            />
            <UpdateDistributionParametersDialog
                distribution={distribution}
                channelId={channelId}
                onClose={() => setOpenParams(false)}
                onConfirm={(parameters: KeyValuePairInput[]) => {
                    setOpenParams(false);
                    setDistribution({
                        ...distribution,
                        parameters: parameters.map((param) => ({ ...param, __typename: "KeyValuePair" })),
                    });
                }}
                open={openParams}
                organizationId={organizationId}
            />
            <DistributionGeoBlockingDialog
                channelId={channelId}
                distribution={distribution}
                onClose={() => {
                    setOpenGeoBlocking(false);
                }}
                onConfirm={(enabled: boolean, mode: AllowDenyMode, countries: string[]) => {
                    setOpenGeoBlocking(false);
                    setDistribution({
                        ...distribution,
                        security: {
                            ...distribution.security,
                            geoBlocking: {
                                ...distribution.security.geoBlocking,
                                enabled,
                                countries,
                                mode,
                            },
                        },
                    });
                }}
                open={openGeoBlocking}
                organizationId={organizationId}
            />
            <DistributionRefererBlockingDialog
                channelId={channelId}
                distribution={distribution}
                onClose={() => setOpenRefererBlocking(false)}
                onConfirm={(enabled: boolean, allowNativeApps, allowedDomains: string[]) => {
                    setOpenRefererBlocking(false);
                    setDistribution({
                        ...distribution,
                        security: {
                            ...distribution.security,
                            refererBlocking: {
                                ...distribution.security.refererBlocking,
                                allowedDomains,
                                allowNativeApps,
                                enabled,
                            },
                        },
                    });
                }}
                open={openRefererBlocking}
                organizationId={organizationId}
            />
            <ConfirmDeleteDialog
                title={distribution.security.skipJwtChecks ? "Resume JWT security" : "Pause JWT security"}
                content={
                    distribution.security.skipJwtChecks
                        ? "Are you sure you want to resume JWT security?"
                        : "Are you sure you want to pause JWT security?"
                }
                onConfirm={() => onConfirmPauseJwtSecurity()}
                onClose={() => setOpenConfirmPauseJwt(false)}
                open={openConfirmPauseJwt}
            />
            <DistributionJwtDialog
                channelId={channelId}
                distribution={distribution}
                onClose={() => {
                    setOpenKeys(false);
                }}
                onConfirm={(keys: { id: string; name?: string }[]) => {
                    setOpenKeys(false);
                    setDistribution({
                        ...distribution,
                        security: {
                            ...distribution.security,
                            keys: keys.map((key) => ({
                                ...key,
                                name: key.name ?? null,
                                __typename: "DistributionSecurityKey",
                            })),
                        },
                    });
                }}
                open={openKeys}
                organizationId={organizationId}
            />
            <UpdateDistributionOutputsDialog
                open={openOutputs}
                onClose={() => setOpenOutputs(false)}
                distribution={distribution}
                organizationId={organizationId}
                channelId={channelId}
                onUpdated={({ hesp, hls, hlsMpegTs }) => {
                    setOpenOutputs(false);
                    setDistribution({
                        ...distribution,
                        outputs: { __typename: "DistributionOutputs", hesp, hls, hlsMpegTs },
                    });
                }}
            />
            <CloneDistributionDialog
                open={openCloneDistribution}
                onClose={() => setOpenCloneDistribution(false)}
                distribution={distribution}
                organizationId={organizationId}
                channelId={channelId}
            />
            <DistributionWebRtcDialog
                channelId={channelId}
                distribution={distribution}
                onClose={() => setOpenWebRtc(false)}
                open={openWebRtc}
                organizationId={organizationId}
                onConfirm={(enabled: boolean, priority: number, name: string, accountId: string, apiUrl: string) => {
                    setOpenWebRtc(false);
                    setDistribution({
                        ...distribution,
                        webRtc: enabled
                            ? null
                            : {
                                  __typename: "DistributionWebRtc",
                                  priority,
                                  src: {
                                      __typename: "DistributionWebRtcSrc",
                                      name,
                                      accountId,
                                      apiUrl,
                                  },
                              },
                    });
                }}
            />
            <DistributionDvrDialog
                channelId={channelId}
                distribution={distribution}
                onClose={() => setOpenDvr(false)}
                open={openDvr}
                organizationId={organizationId}
                onConfirm={(enabled: boolean) => {
                    setOpenDvr(false);
                    setDistribution({
                        ...distribution,
                        dvr: enabled
                            ? {
                                  __typename: "DistributionDvr",
                                  enabled,
                              }
                            : null,
                    });
                }}
            />
            <DistributionIpBlockingDialog
                channelId={channelId}
                distribution={distribution}
                onClose={() => {
                    setOpenIpBlocking(false);
                }}
                onConfirm={(enabled: boolean, mode: AllowDenyMode, cidrs: string[]) => {
                    setOpenIpBlocking(false);
                    setDistribution({
                        ...distribution,
                        security: {
                            ...distribution.security,
                            ipBlocking: {
                                ...distribution.security.ipBlocking,
                                enabled,
                                cidrs,
                                mode,
                            },
                        },
                    });
                }}
                open={openIpBlocking}
                organizationId={organizationId}
            />
            <div className="cursor-pointer" onClick={() => setShowMore(!showMore)}>
                {showMore ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </div>
        </div>
    );
}

export const UPDATE_DISTRIBUTION = gql`
    mutation UpdateV2Distribution($input: UpdateDistributionInput!) {
        updateV2Distribution(input: $input)
    }
`;
